import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, eur, errMsg } from "@/lib/api";
import { OPTION_LT, toggleOption } from "@/lib/options";

const input = "w-full h-10 px-3 bg-[#1F2230] border border-[#3B4157] text-sm outline-none focus:border-orange-500";

export function ApproveDialog({ inq, onClose, onDone }) {
  const [products, setProducts] = useState([]);
  const [pid, setPid] = useState(inq.product?.id || "");
  const [opts, setOpts] = useState((inq.options || []).map((o) => o.key));
  const [price, setPrice] = useState(inq.offer_price || inq.total || "");
  const [note, setNote] = useState("");
  const [prices, setPrices] = useState({});
  const [days, setDays] = useState(14);
  const until = new Date(Date.now() + Number(days || 0) * 864e5).toISOString().slice(0, 10);
  const optPrice = (o) => (prices[o.key] ?? o.price);
  const [busy, setBusy] = useState(false);
  const p = products.find((x) => x.id === pid);
  const avail = (p?.options || []).filter((o) => o.enabled);

  useEffect(() => { api.get("/admin/products").then((r) => setProducts(r.data)); }, []);

  const sale = (prod) => prod.price * (1 - (prod.discount_pct || 0) / 100);
  const recalc = (prod, sel, pr = prices) => prod && setPrice((sale(prod) + prod.options.filter((o) => sel.includes(o.key)).reduce((s, o) => s + Number(pr[o.key] ?? o.price), 0)).toFixed(2));
  const setOptPrice = (k, v) => { const pr = { ...prices, [k]: v }; setPrices(pr); recalc(p, opts, pr); };
  const pick = (id) => { const np = products.find((x) => x.id === id); setPid(id); const sel = opts.filter((k) => np?.options.some((o) => o.key === k && o.enabled)); setOpts(sel); recalc(np, sel); };
  const tog = (k) => { const sel = toggleOption(opts, k); setOpts(sel); recalc(p, sel); };

  const submit = async () => {
    if (!pid || !(Number(price) > 0)) return toast.error("Pasirinkite sėdynę ir kainą");
    if (!(Number(days) >= 1 && Number(days) <= 90)) return toast.error("Galiojimas 1–90 dienų");
    setBusy(true);
    try {
      await api.post(`/admin/inquiries/${inq.id}/approve`, { product_id: pid, options: opts, price: Number(price), note, valid_days: Number(days) });
      toast.success("Pasiūlymas su apmokėjimo nuoroda išsiųstas klientui");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="approve-dialog" className="bg-[#292D3C] border-[#3B4157] max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display uppercase text-2xl">Pasiūlymas pagal užklausą {inq.number}</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <label className="block text-xs uppercase tracking-wider text-slate-400 space-y-1.5"><span>Sėdynė</span>
            <select data-testid="approve-product-select" className={input} value={pid} onChange={(e) => pick(e.target.value)}>
              <option value="">— pasirinkite —</option>
              {products.map((x) => <option key={x.id} value={x.id}>{x.name}{x.fits ? ` · ${x.sku}` : ""} ({x.price_on_request ? "Klausti kainos" : `${eur(sale(x))} be PVM${x.discount_pct > 0 ? ` (−${x.discount_pct}%)` : ""}`})</option>)}
            </select></label>
          {avail.length > 0 && (
            <div className="space-y-1.5">{avail.map((o) => (
              <label key={o.key} className="flex items-center gap-2 text-slate-300">
                <input type="checkbox" data-testid={`approve-option-${o.key}`} checked={opts.includes(o.key)} onChange={() => tog(o.key)} className="accent-orange-500 w-4 h-4" />
                <span className="flex-1">{OPTION_LT[o.key]}</span>
                <span className="text-slate-500">+</span>
                <input data-testid={`approve-option-price-${o.key}`} type="number" min="0" step="0.01" value={optPrice(o)} onChange={(e) => setOptPrice(o.key, e.target.value)}
                  className="w-24 h-8 px-2 text-right bg-[#1F2230] border border-[#3B4157] font-mono-x outline-none focus:border-orange-500" /><span className="text-slate-500">€</span>
              </label>))}
              <p className="text-xs text-slate-500">Kainas galite pakeisti šiam pasiūlymui. Nuolatines priedų kainas keiskite skiltyje „Priedai“.</p>
            </div>
          )}
          <label className="block text-xs uppercase tracking-wider text-slate-400 space-y-1.5"><span>Galutinė kaina su priedais be PVM, EUR</span>
            <input data-testid="approve-price-input" type="number" step="0.01" min="0" className={input} value={price} onChange={(e) => setPrice(e.target.value)} />
            {Number(price) > 0 && <span data-testid="approve-price-gross" className="block normal-case tracking-normal text-slate-500">Klientas mokės su 21 % PVM ir 18 € pristatymu: {eur(Math.round(Number(price) * 121) / 100 + 18)}</span>}</label>
          <label className="block text-xs uppercase tracking-wider text-slate-400 space-y-1.5"><span>Pasiūlymas galioja (dienų)</span>
            <input data-testid="approve-valid-days-input" type="number" min="1" max="90" className={input} value={days} onChange={(e) => setDays(e.target.value)} />
            <span data-testid="approve-valid-until" className="block normal-case tracking-normal text-slate-500">Galioja iki {until}. Likus 3 dienoms klientui automatiškai išsiųsime priminimą, jei dar nebus apmokėjęs.</span></label>
          <label className="block text-xs uppercase tracking-wider text-slate-400 space-y-1.5"><span>Žinutė klientui (nebūtina)</span>
            <textarea data-testid="approve-note-input" rows={3} className={`${input} h-auto py-2`} value={note} onChange={(e) => setNote(e.target.value)} /></label>
          <button data-testid="approve-submit-btn" onClick={submit} disabled={busy} className="w-full h-11 bg-orange-500 text-[#1F2230] font-display font-bold uppercase tracking-widest hover:bg-orange-400 disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Siųsti pasiūlymą klientui
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
